'use client';

import React from 'react';
import { Tool, SnapSize, WallType } from '@/types';
import { WALL_CONSTRUCTIONS, getExteriorWalls, getInteriorWalls } from '@/lib/wall-constructions';

interface ToolbarProps {
  activeTool: Tool;
  onToolChange: (tool: Tool) => void;
  snapSize: SnapSize;
  onSnapChange: (size: SnapSize) => void;
  showGrid: boolean;
  onGridToggle: () => void;
  selectedWallType: string;
  onWallTypeChange: (id: string) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

const SNAP_OPTIONS: { value: SnapSize; label: string }[] = [
  { value: 0.01, label: '1 cm' },
  { value: 0.05, label: '5 cm' },
  { value: 0.1, label: '10 cm' },
  { value: 0.2, label: '20 cm' },
  { value: 0.4, label: '40 cm' },
  { value: 1.0, label: '1 m' },
];

const TOOLS: { id: Tool; label: string; icon: string; shortcut?: string }[] = [
  { id: 'select', label: 'Markera', icon: '⬚', shortcut: 'V' },
  { id: 'wall', label: 'Vägg', icon: '▬', shortcut: 'W' },
  { id: 'door', label: 'Dörr', icon: '🚪', shortcut: 'D' },
  { id: 'window', label: 'Fönster', icon: '◻', shortcut: 'F' },
  { id: 'erase', label: 'Radera', icon: '✕', shortcut: 'E' },
];

/** Visual thickness indicator bar */
function ThicknessPreview({ thickness, isActive }: { thickness: number; isActive: boolean }) {
  // Scale: 100mm = 3px, 300mm = 9px (capped)
  const height = Math.min(Math.max(Math.round(thickness / 33), 3), 10);
  return (
    <div className="flex items-center justify-center w-5">
      <div
        className={`rounded-sm transition-colors ${
          isActive ? 'bg-blue-400' : 'bg-gray-500'
        }`}
        style={{ width: '3px', height: `${height}px` }}
      />
    </div>
  );
}

export default function Toolbar({
  activeTool,
  onToolChange,
  snapSize,
  onSnapChange,
  showGrid,
  onGridToggle,
  selectedWallType,
  onWallTypeChange,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
}: ToolbarProps) {
  const exteriorWalls = getExteriorWalls();
  const interiorWalls = getInteriorWalls();

  return (
    <div className="w-64 bg-gray-900 text-white flex flex-col h-full overflow-y-auto border-r border-gray-800">
      {/* Tools */}
      <div className="p-3">
        <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-2.5 px-0.5">
          Verktyg
        </h3>
        <div className="grid grid-cols-5 gap-1">
          {TOOLS.map((tool) => (
            <button
              key={tool.id}
              onClick={() => onToolChange(tool.id)}
              className={`aspect-square rounded-lg text-lg flex flex-col items-center justify-center transition-all ${
                activeTool === tool.id
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/30 ring-1 ring-blue-500/30'
                  : 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-200'
              }`}
              title={`${tool.label} (${tool.shortcut})`}
            >
              <span className="text-base leading-none">{tool.icon}</span>
              <span className="text-[8px] mt-0.5 opacity-70">{tool.shortcut}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mx-3 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" />

      {/* Undo / Redo */}
      <div className="p-3">
        <div className="flex gap-1.5">
          <button
            onClick={onUndo}
            disabled={!canUndo}
            className="flex-1 px-2.5 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-md text-xs disabled:opacity-25 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
          >
            <span className="text-gray-400">↩</span> Ångra
          </button>
          <button
            onClick={onRedo}
            disabled={!canRedo}
            className="flex-1 px-2.5 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-md text-xs disabled:opacity-25 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
          >
            Gör om <span className="text-gray-400">↪</span>
          </button>
        </div>
      </div>

      <div className="mx-3 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" />

      {/* Wall Type Selection */}
      <div className="p-3">
        <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-2.5 px-0.5">
          Väggkonstruktion
        </h3>

        {/* Exterior walls */}
        <div className="mb-3">
          <p className="text-[10px] text-gray-500 mb-1.5 px-0.5 uppercase tracking-wider">Ytterväggar</p>
          <div className="space-y-0.5">
            {exteriorWalls.map((wall) => {
              const isActive = selectedWallType === wall.id;
              return (
                <label
                  key={wall.id}
                  className={`flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer text-sm transition-all ${
                    isActive
                      ? 'bg-blue-600/20 text-blue-200 ring-1 ring-blue-500/20'
                      : 'hover:bg-gray-800 text-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="wallType"
                    value={wall.id}
                    checked={isActive}
                    onChange={() => onWallTypeChange(wall.id)}
                    className="accent-blue-500 sr-only"
                  />
                  <ThicknessPreview thickness={wall.thickness} isActive={isActive} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium ${isActive ? 'text-blue-200' : 'text-gray-200'}`}>{wall.name}</div>
                    <div className={`text-[10px] ${isActive ? 'text-blue-400/70' : 'text-gray-500'}`}>{wall.thickness} mm</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Interior walls */}
        <div>
          <p className="text-[10px] text-gray-500 mb-1.5 px-0.5 uppercase tracking-wider">Innerväggar</p>
          <div className="space-y-0.5">
            {interiorWalls.map((wall) => {
              const isActive = selectedWallType === wall.id;
              return (
                <label
                  key={wall.id}
                  className={`flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer text-sm transition-all ${
                    isActive
                      ? 'bg-blue-600/20 text-blue-200 ring-1 ring-blue-500/20'
                      : 'hover:bg-gray-800 text-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="wallType"
                    value={wall.id}
                    checked={isActive}
                    onChange={() => onWallTypeChange(wall.id)}
                    className="accent-blue-500 sr-only"
                  />
                  <ThicknessPreview thickness={wall.thickness} isActive={isActive} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium ${isActive ? 'text-blue-200' : 'text-gray-200'}`}>{wall.name}</div>
                    <div className={`text-[10px] ${isActive ? 'text-blue-400/70' : 'text-gray-500'}`}>{wall.thickness} mm</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mx-3 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" />

      {/* Snap Settings */}
      <div className="p-3">
        <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-2.5 px-0.5">
          Snapping
        </h3>
        <div className="grid grid-cols-3 gap-1">
          {SNAP_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onSnapChange(opt.value)}
              className={`px-2 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                snapSize === opt.value
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20'
                  : 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-3 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" />

      {/* Grid Toggle */}
      <div className="p-3">
        <label className="flex items-center gap-2.5 cursor-pointer px-0.5">
          <input
            type="checkbox"
            checked={showGrid}
            onChange={onGridToggle}
            className="accent-blue-500 w-3.5 h-3.5 rounded"
          />
          <span className="text-xs text-gray-300">Visa rutnät</span>
        </label>
      </div>

      <div className="flex-1" />

      {/* Keyboard Shortcuts */}
      <div className="p-3 border-t border-gray-800">
        <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-2.5 px-0.5">
          Genvägar
        </h3>
        <div className="text-[11px] text-gray-500 space-y-1.5 px-0.5">
          {[
            ['V', 'Markera'],
            ['W', 'Vägg'],
            ['D', 'Dörr'],
            ['F', 'Fönster'],
            ['E', 'Radera'],
            ['Esc', 'Avbryt'],
            ['Del', 'Ta bort'],
          ].map(([key, label]) => (
            <div key={key} className="flex items-center justify-between">
              <span>{label}</span>
              <kbd className="bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded text-[10px] font-mono border border-gray-700">{key}</kbd>
            </div>
          ))}
          {(() => {
            const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const mod = isMac ? '⌘' : 'Ctrl+';
            return (
              <>
                <div className="flex items-center justify-between">
                  <span>Ångra</span>
                  <kbd className="bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded text-[10px] font-mono border border-gray-700">{mod}Z</kbd>
                </div>
                <div className="flex items-center justify-between">
                  <span>Gör om</span>
                  <kbd className="bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded text-[10px] font-mono border border-gray-700">{mod}⇧Z</kbd>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
